/**
 * Portal-side fetch wrapper.
 *
 * Employee portal pages (`/portal/*`) use a SEPARATE JWT from admin
 * users - stored under `hf-portal:token` instead of `hf-app:access-token`.
 * Calling `fetchApi` directly from a portal hook sends the ADMIN bearer
 * (or none if the admin is logged out), the BE PortalJwtGuard rejects
 * with 401, and `fetchApi`'s 401-handler used to clear admin storage
 * and force-redirect to /login.
 *
 * This wrapper:
 *   1. Pulls the portal JWT out of the portal session (portalAuthApi owns where
 *      that lives: shared storage for an employee, the tab's own for an admin
 *      preview).
 *   2. Forwards through `fetchApi` with `skipAuth: true` so the admin
 *      token isn't injected and the 401-handler doesn't nuke admin auth.
 *   3. Adds the portal Authorization header as a plain string entry -
 *      `new Headers()` would silently lose it because `fetchApi` spreads
 *      the headers as a plain object (`{...headers}`), and Headers
 *      instances don't expose own enumerable properties.
 *   4. RENEWS AN EXPIRED TOKEN (17 Oct 2026). The access token lives 8 hours.
 *      This wrapper used to send it as it was, so after the first expiry the
 *      news, the library, the assistant, policies, surveys and the practice
 *      emails all read 401 - and the screens say what they say for a 404,
 *      "removed". On a 401 it now renews ONCE with the stored refresh token
 *      (portalAuthApi.refresh: single-flight, so parallel reads share one
 *      renewal) and retries the read ONCE. Never a loop.
 *
 * Use this for every `/v1/portal/*` (employee-facing) endpoint.
 * Admin-facing portal endpoints like `/v1/portal-news` (workspace CRUD)
 * still go through plain `fetchApi`.
 */

import { ApiClientError, fetchApi } from './client';
import { portalAuthApi } from './portal-auth';
import { PORTAL_LOGIN_PATH, portalSignInHref } from '@/lib/user-portal/portal-login-redirect';

/** The `errorCode` of a read that found the portal session over. */
export const PORTAL_SIGNED_OUT_CODE = 'portal_signed_out';

/**
 * The portal session is over: the token was refused and could not be renewed
 * (or there is nothing to renew it with - a person signed in by a code has no
 * refresh token).
 *
 * It is the API client's own error (ApiClientError, status 401) wearing the code
 * `portal_signed_out`, so every caller that already treats a 401 as "signed out"
 * (the surveys and policies screens do) keeps doing so; a caller that wants the
 * sign-in link asks `isPortalSignedOutError` and uses `portalSignedOutHref()`.
 *
 * BUILT WHEN NEEDED, not as a subclass declared at module scope: dozens of specs
 * replace '@/lib/api/client' with only `{ fetchApi }`, and a
 * `class … extends ApiClientError` evaluated at import killed every suite that
 * reached this file through a hook ("Class extends value undefined"). Resolving
 * the class when the error is made means a partial mock cannot break module load.
 */
export type PortalSignedOutError = Error & { statusCode: 401; errorCode: typeof PORTAL_SIGNED_OUT_CODE };

function makeSignedOutError(): PortalSignedOutError {
  const body = {
    statusCode: 401,
    errorCode: PORTAL_SIGNED_OUT_CODE,
    message: 'Your portal session has ended',
  };
  const error: Error & { statusCode?: number; statusText?: string; body?: unknown; errorCode?: string | null } =
    typeof ApiClientError === 'function'
      ? new ApiClientError(401, 'Unauthorized', body)
      : Object.assign(new Error(body.message), { statusCode: 401, statusText: 'Unauthorized', body, errorCode: PORTAL_SIGNED_OUT_CODE });
  error.name = 'PortalSignedOutError';
  return error as PortalSignedOutError;
}

export function isPortalSignedOutError(err: unknown): err is PortalSignedOutError {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { errorCode?: unknown }).errorCode === PORTAL_SIGNED_OUT_CODE
  );
}

/**
 * Where a caller sends a person whose read ended in a signed-out error:
 * the portal sign-in, carrying the page they were on (`next`) and their
 * workspace (`ws`), so signing in brings them back here instead of to a home
 * page that does not know what they were doing.
 */
export function portalSignedOutHref(): string {
  if (typeof window === 'undefined') return PORTAL_LOGIN_PATH;
  return portalSignInHref(window.location.pathname, window.location.search);
}

/** A 401 the way the API client reports it (read off the error, so it needs no class from the client module). */
function isUnauthorized(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { statusCode?: unknown }).statusCode === 401;
}

function send<T>(path: string, init: RequestInit, token: string | null): Promise<T> {
  const existing =
    init.headers instanceof Headers
      ? Object.fromEntries(init.headers.entries())
      : (init.headers as Record<string, string>) ?? {};
  const headers: Record<string, string> = { ...existing };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetchApi<T>(path, { ...init, headers, skipAuth: true });
}

/** End this session - but only if it is still the one that was refused; a newer sign-in is not ours to erase. */
function endSession(refused: string | null): PortalSignedOutError {
  if (refused && portalAuthApi.getToken() === refused) portalAuthApi.clearSession();
  return makeSignedOutError();
}

export async function fetchPortalApi<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const sent = portalAuthApi.getToken();
  try {
    return await send<T>(path, init, sent);
  } catch (err) {
    if (!isUnauthorized(err)) throw err;

    // The token was refused. If another read has already renewed the session
    // since this one went out, the stored token is the answer; otherwise renew.
    const stored = portalAuthApi.getToken();
    const renewed = stored && stored !== sent ? stored : await portalAuthApi.refresh();
    if (!renewed) throw endSession(sent);

    try {
      return await send<T>(path, init, renewed);
    } catch (retryErr) {
      // Refused again with a fresh token: the session is over. No third attempt.
      if (isUnauthorized(retryErr)) throw endSession(renewed);
      throw retryErr;
    }
  }
}
